import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  Shield,
  Building2,
  Users,
  CheckCircle,
  Phone,
  Mail,
  MapPin,
  ArrowRight,
  ClipboardCheck,
  Calendar,
  Target,
  Award,
  Clock,
  Send
} from 'lucide-react';

const Welcome = () => {
  const navigate = useNavigate();
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/enquiries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contactForm),
      });
      
      if (response.ok) {
        setFormSubmitted(true);
        setContactForm({ name: '', email: '', phone: '', subject: '', message: '' });
        setTimeout(() => setFormSubmitted(false), 5000);
      } else {
        const error = await response.json();
        alert('Failed to send message: ' + (error.detail || 'Please try again.'));
      }
    } catch (error) {
      console.error('Error submitting enquiry:', error);
      alert('Failed to send message. Please try again or contact us directly.');
    }
  };

  const services = [
    {
      icon: FileText,
      title: 'Taxation & Compliance',
      description: 'GST, TDS, Income-tax and statutory filings handled with systematic tracking and verification.'
    },
    {
      icon: Shield,
      title: 'Audit & Assurance Support',
      description: 'Assistance in statutory, internal and regulatory audit requirements with structured documentation and review processes.'
    },
    {
      icon: Building2,
      title: 'Regulatory & Institutional Compliance',
      description: 'Support for universities, trusts, societies and entities requiring adherence to government and regulatory frameworks.'
    },
    {
      icon: Users,
      title: 'Advisory & Representation',
      description: 'Professional guidance and departmental representations in taxation and compliance matters.'
    }
  ];

  const strengths = [
    'Process-driven compliance management',
    'Clear responsibility tracking',
    'Institutional engagement experience',
    'Consistent documentation standards',
    'Reliable statutory follow-up',
    'Professional communication discipline'
  ];

  const workflowFeatures = [
    { icon: Calendar, text: 'Statutory deadlines' },
    { icon: ClipboardCheck, text: 'Task assignments' },
    { icon: FileText, text: 'Document status' },
    { icon: Target, text: 'Client obligations' },
    { icon: Clock, text: 'Completion tracking' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="text-xl font-bold text-gray-900">
                <span className="text-blue-800">HYDER ALI</span>
                <span className="text-gray-600"> AND ASSOCIATES</span>
              </div>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#services" className="text-gray-600 hover:text-blue-800 transition-colors">Services</a>
              <a href="#about" className="text-gray-600 hover:text-blue-800 transition-colors">About</a>
              <a href="#approach" className="text-gray-600 hover:text-blue-800 transition-colors">Our Approach</a>
              <a href="#contact" className="text-gray-600 hover:text-blue-800 transition-colors">Contact</a>
              <button
                onClick={() => navigate('/login')}
                className="bg-blue-800 text-white px-5 py-2 rounded-md hover:bg-blue-900 transition-colors font-medium"
              >
                Client Login
              </button>
            </div>
            <div className="md:hidden">
              <button
                onClick={() => navigate('/login')}
                className="bg-blue-800 text-white px-4 py-2 rounded-md hover:bg-blue-900 transition-colors text-sm font-medium"
              >
                Login
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-gray-50 to-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="flex justify-center mb-6">
              <img src="/images/ca-logo.png" alt="Chartered Accountants India" className="h-16 sm:h-20 w-auto" />
            </div>
            <p className="text-blue-800 font-semibold tracking-wider uppercase mb-4">Chartered Accountants</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              HYDER ALI AND ASSOCIATES
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-4 font-light">
              Reliable Compliance. Structured Processes. Trusted Advisory.
            </p>
            <p className="text-lg text-gray-500 mb-10 max-w-3xl mx-auto leading-relaxed">
              We provide taxation, audit and regulatory compliance services with a disciplined, 
              technology-enabled workflow to ensure accuracy, accountability and timely execution.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="#contact"
                className="bg-blue-800 text-white px-8 py-4 rounded-md hover:bg-blue-900 transition-colors font-medium text-lg inline-flex items-center justify-center"
              >
                Contact Us
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
              <a
                href="#services"
                className="border-2 border-blue-800 text-blue-800 px-8 py-4 rounded-md hover:bg-blue-50 transition-colors font-medium text-lg"
              >
                Our Services
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Firm Introduction */}
      <section id="about" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              A Professional Practice Built on Process and Accountability
            </h2>
            <div className="w-20 h-1 bg-blue-800 mx-auto mb-8"></div>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              HYDER ALI AND ASSOCIATES is a Chartered Accountant firm based in Kerala, providing 
              comprehensive services in taxation, statutory compliance, audit support and regulatory advisory.
            </p>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              Our practice is structured around defined workflows, monitored deadlines and documented 
              processes, enabling consistent service delivery across assignments and clients.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              We work with institutions, organisations, professionals and businesses requiring 
              dependable compliance support and clear professional guidance.
            </p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Our Core Services</h2>
            <div className="w-20 h-1 bg-blue-800 mx-auto"></div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <div 
                key={index} 
                className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-100"
              >
                <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                  <service.icon className="w-7 h-7 text-blue-800" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Work Approach Section */}
      <section id="approach" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Our Structured Work Methodology
              </h2>
              <div className="w-20 h-1 bg-blue-800 mb-8"></div>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                We operate through a disciplined internal compliance management system that monitors 
                all aspects of client engagements, ensuring nothing falls through the cracks.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {workflowFeatures.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    <feature.icon className="w-5 h-5 text-blue-800" />
                    <span className="text-gray-700">{feature.text}</span>
                  </div>
                ))}
              </div>
              <p className="text-gray-600 mt-8 leading-relaxed">
                This structured workflow reduces dependency risks and ensures continuity, 
                accountability and timely execution of engagements.
              </p>
            </div>
            <div className="bg-gradient-to-br from-blue-800 to-blue-900 p-10 rounded-2xl text-white">
              <Award className="w-12 h-12 mb-6 opacity-80" />
              <h3 className="text-2xl font-bold mb-6">Professional Strengths</h3>
              <ul className="space-y-4">
                {strengths.map((strength, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-3 mt-0.5 text-blue-300 flex-shrink-0" />
                    <span className="text-blue-100">{strength}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 pt-6 border-t border-blue-700">
                <p className="text-blue-200 text-sm leading-relaxed">
                  Our objective is not only filing compliance, but ensuring it is completed 
                  correctly, on time and with full traceability.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Note */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Technology-Enabled Professional Practice</h3>
          <p className="text-gray-600 leading-relaxed">
            Our firm utilises an internal practice management platform to monitor assignments, 
            statutory due dates and documentation across clients. This enables systematic tracking, 
            accountability and service continuity.
          </p>
        </div>
      </section>

      {/* Firm Philosophy */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Commitment</h2>
          <div className="w-20 h-1 bg-blue-800 mx-auto mb-8"></div>
          <p className="text-xl text-gray-600 leading-relaxed italic">
            "We believe compliance is not a one-time activity but a continuous professional responsibility. 
            Our approach emphasises clarity, discipline and dependable execution in every engagement."
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Get In Touch</h2>
            <div className="w-20 h-1 bg-blue-800 mx-auto mb-6"></div>
            <p className="text-gray-600">Enquiries and professional consultations are welcome.</p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">HYDER ALI AND ASSOCIATES</h3>
                <p className="text-blue-800 font-medium mb-8">Chartered Accountants</p>
                
                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-blue-800" />
                    </div>
                    <div className="ml-4">
                      <p className="font-medium text-gray-900">Location</p>
                      <p className="text-gray-600">Kerala, India</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-blue-800" />
                    </div>
                    <div className="ml-4">
                      <p className="font-medium text-gray-900">Email</p>
                      <a href="mailto:cahyderali@gmail.com" className="text-blue-800 hover:underline">
                        cahyderali@gmail.com
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-blue-800" />
                    </div>
                    <div className="ml-4">
                      <p className="font-medium text-gray-900">Phone</p>
                      <a href="tel:+918075874260" className="text-blue-800 hover:underline">
                        +91 8075874260
                      </a>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-8 border-t border-gray-100">
                  <button
                    onClick={() => navigate('/login')}
                    className="w-full bg-blue-800 text-white py-3 rounded-md hover:bg-blue-900 transition-colors font-medium"
                  >
                    Existing Client? Login Here
                  </button>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Send us a Message</h3>
              
              {formSubmitted ? (
                <div className="bg-green-50 text-green-800 p-6 rounded-lg text-center">
                  <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-600" />
                  <p className="font-medium text-lg">Thank you for your message!</p>
                  <p className="text-green-600 mt-2">We will get back to you shortly.</p>
                </div>
              ) : (
                <form onSubmit={handleContactSubmit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                      <input
                        type="text"
                        required
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                      <input
                        type="tel"
                        required
                        value={contactForm.phone}
                        onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        placeholder="Your phone number"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <input
                      type="email"
                      required
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                    <input
                      type="text"
                      value={contactForm.subject}
                      onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder="How can we help?"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                    <textarea
                      required
                      rows={4}
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                      placeholder="Your message..."
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full bg-blue-800 text-white py-3 rounded-md hover:bg-blue-900 transition-colors font-medium flex items-center justify-center"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    Send Message
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h3 className="text-xl font-bold mb-2">HYDER ALI AND ASSOCIATES</h3>
            <p className="text-gray-400 mb-4">Chartered Accountants</p>
            <p className="text-gray-500 text-sm">
              Taxation • Compliance • Advisory • Audit Support
            </p>
            <div className="mt-8 pt-8 border-t border-gray-800">
              <p className="text-gray-500 text-sm">
                © {new Date().getFullYear()} Hyder Ali And Associates. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Welcome;
